// ═══════════════════════════════════════════════════════════════════
//  TOQUM · TRAINING & CERTIFICATION
//  Course library (video · reading · podcast) with assessments,
//  admin-defined grading yardsticks, per-programme certificates and
//  the CQB pathway — Certified QA of Brand — for supplier QAs working
//  dedicatedly on a brand's orders.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import {
  T, CC, uid, today, addDays, num, Page, CardRow, Split, Section, Field, Grid, Btn,
  DataTable, TabBar, Tag, Badge, StatCard, DynamicTable, Collapse, Modal, Toast,
  RatingStars, printHTML, apiPost, isManager, isSuperAdmin, hasRight,
} from './Shared';

// ── Course catalogue shipped with the product ────────────────────
export const COURSE_CATEGORIES = [
  'Brand Quality Systems','Quality Tools','Root Cause Analysis','Inspection & AQL',
  'Project Management','Conflict Management','Ethical Behaviour','Feedback & Coaching',
  'Compliance & Safety','Technical Skills',
];

export const CONTENT_TYPES = [
  { key:'video',    label:'Video',            icon:'▶',  color:'#e2445c' },
  { key:'reading',  label:'Reading Material', icon:'📖', color:'#0073ea' },
  { key:'podcast',  label:'Podcast',          icon:'🎧', color:'#a25ddc' },
  { key:'document', label:'Document / SOP',   icon:'📄', color:'#0da99b' },
];

export const LEVELS = ['Foundation','Intermediate','Advanced','Expert'];

// Default library — admin can add, edit and remove
export const DEFAULT_COURSES = [
  { code:'BQS-101', title:'Brand Quality Systems — Fundamentals', category:'Brand Quality Systems',
    level:'Foundation', duration:90,
    description:'How a brand defines its quality standard, the process flow from PO to shipment, '
      + 'the role of each inspection stage and what "approved" actually means.',
    objectives:['Explain the brand quality process end to end','Identify each mandatory gate and its purpose',
      'Read and apply a brand quality manual','Understand escalation and exception routes'] },
  { code:'BQS-201', title:'Brand Quality Systems — Applied Auditing', category:'Brand Quality Systems',
    level:'Intermediate', duration:120,
    description:'Conducting a factory quality audit against a brand standard, scoring, '
      + 'grading and writing findings that lead to real corrective action.',
    objectives:['Score an audit section objectively','Distinguish critical from major findings',
      'Write a finding that a factory can act on','Grade and communicate an audit result'] },
  { code:'RCA-101', title:'Root Cause Analysis — 5 Whys & Fishbone', category:'Root Cause Analysis',
    level:'Foundation', duration:75,
    description:'Getting past the symptom. Structured questioning, cause-and-effect diagrams '
      + 'and the discipline of evidence.',
    objectives:['Run a 5 Whys to a genuine root cause','Build a fishbone across the 6M categories',
      'Separate cause from correlation','Document findings for a CAP'] },
  { code:'RCA-201', title:'Root Cause Analysis — 8D & Corrective Action', category:'Root Cause Analysis',
    level:'Intermediate', duration:120,
    description:'The 8D discipline, containment versus correction versus prevention, '
      + 'and verifying that an action actually worked.',
    objectives:['Run the eight disciplines in order','Design containment that protects the customer',
      'Write preventive action that stops recurrence','Verify and close with evidence'] },
  { code:'QT-101', title:'Seven Basic Quality Tools', category:'Quality Tools',
    level:'Foundation', duration:110,
    description:'Check sheet, histogram, Pareto, cause-and-effect, scatter, control chart, stratification — '
      + 'and when each one is the right tool.',
    objectives:['Choose the correct tool for a problem','Build a Pareto and read the vital few',
      'Interpret a control chart','Present data honestly'] },
  { code:'QT-201', title:'FMEA & Risk Assessment', category:'Quality Tools',
    level:'Intermediate', duration:100,
    description:'Failure modes, effects, severity/occurrence/detection scoring, RPN and '
      + 'prioritising action where it matters.',
    objectives:['Identify failure modes in a process step','Score S, O and D defensibly',
      'Calculate and act on RPN','Link FMEA to the control plan'] },
  { code:'QT-202', title:'Statistical Process Control & Capability', category:'Quality Tools',
    level:'Advanced', duration:140,
    description:'Variation, control limits, capability indices and what a process is actually telling you.',
    objectives:['Distinguish common from special cause','Build and read X-bar and R charts',
      'Interpret Cp and Cpk','Respond to an out-of-control signal'] },
  { code:'INS-101', title:'AQL Sampling & Inspection Discipline', category:'Inspection & AQL',
    level:'Foundation', duration:95,
    description:'Sampling plans, inspection levels, accept and reject numbers, and why '
      + 'the sample must be drawn properly.',
    objectives:['Select the correct sampling plan','Read accept/reject from the table',
      'Draw a representative sample','Classify defects consistently'] },
  { code:'INS-201', title:'Defect Classification & Measurement Audit', category:'Inspection & AQL',
    level:'Intermediate', duration:105,
    description:'Critical, major and minor — applied consistently. Plus measuring garments '
      + 'against a spec and tolerance.',
    objectives:['Classify a defect the same way every time','Measure to a POM correctly',
      'Judge a measurement against tolerance','Document findings with evidence'] },
  { code:'PM-101', title:'Project Management for Quality Teams', category:'Project Management',
    level:'Foundation', duration:120,
    description:'Planning a quality intervention, sequencing work backwards from ex-factory, '
      + 'tracking and reporting.',
    objectives:['Build a backward plan from a ship date','Identify the critical path',
      'Track progress against a baseline','Report status without hiding risk'] },
  { code:'CM-101', title:'Conflict Management in the Supply Chain', category:'Conflict Management',
    level:'Foundation', duration:85,
    description:'Handling disagreement with factories and internal stakeholders without '
      + 'damaging the relationship or the standard.',
    objectives:['Separate the person from the problem','Hold a standard under pressure',
      'De-escalate a heated inspection dispute','Reach agreement that survives the visit'] },
  { code:'CM-201', title:'Negotiation & Difficult Conversations', category:'Conflict Management',
    level:'Intermediate', duration:100,
    description:'Preparing for a hard conversation, claim discussions and reaching '
      + 'settlements that both sides honour.',
    objectives:['Prepare interests, not just positions','Deliver bad news clearly and early',
      'Handle a claim conversation','Close with a written, shared understanding'] },
  { code:'ETH-101', title:'Ethical Behaviour & Integrity in Quality', category:'Ethical Behaviour',
    level:'Foundation', duration:80,
    description:'Gifts, pressure to pass, falsified records, and what to do when you are '
      + 'asked to look the other way.',
    objectives:['Recognise a conflict of interest','Refuse improper inducement',
      'Detect falsified inspection records','Use the escalation route safely'] },
  { code:'FB-101', title:'Giving & Receiving Feedback', category:'Feedback & Coaching',
    level:'Foundation', duration:70,
    description:'Feedback that changes behaviour — specific, timely, and about the work.',
    objectives:['Give specific behavioural feedback','Receive criticism without defensiveness',
      'Coach a line QC to improve','Follow up so feedback sticks'] },
  { code:'CS-101', title:'Product Safety & Compliance Essentials', category:'Compliance & Safety',
    level:'Foundation', duration:90,
    description:'Needle control, sharp tools, metal detection, pull testing, restricted '
      + 'substances and drawcord safety.',
    objectives:['Run a needle control system','Verify metal detector calibration',
      'Conduct a pull test correctly','Identify a safety-critical failure'] },
];

// Question bank seeds — admin can add more per course
const SEED_QUESTIONS = {
  'RCA-101':[
    { q:'A seam fails after wash. Which is a root cause rather than a symptom?',
      options:['Seams are opening in the field','Stitch density was set below specification and never verified',
               'Customers are returning garments','The inspection failed'],
      answer:1, marks:2 },
    { q:'In a fishbone diagram, which category would "operator not trained on the new folder" fall under?',
      options:['Machine','Material','Manpower','Measurement'], answer:2, marks:1 },
    { q:'How do you know you have reached a genuine root cause with 5 Whys?',
      options:['You have asked exactly five questions','Removing it would prevent recurrence',
               'Everyone in the room agrees','It identifies who is responsible'], answer:1, marks:2 },
  ],
  'INS-101':[
    { q:'A lot of 3,000 pcs is inspected at AQL 2.5, General Level II. What is the sample size?',
      options:['80','125','200','315'], answer:1, marks:2 },
    { q:'Sampling must be drawn:',
      options:['From the cartons nearest the door','Proportionally across colours, sizes and cartons',
               'From whatever the factory presents','From the last carton packed'], answer:1, marks:2 },
    { q:'One critical defect is found in a sample. The correct action is:',
      options:['Accept, it is only one','Reject the lot','Ignore it if majors are within limit',
               'Re-inspect a smaller sample'], answer:1, marks:3 },
  ],
  'ETH-101':[
    { q:'A factory offers to cover your hotel while you inspect their order. You should:',
      options:['Accept, it is normal hospitality','Decline and record the offer',
               'Accept but stay objective','Accept if the inspection passes anyway'], answer:1, marks:3 },
    { q:'You discover inspection reports signed before the inspection took place. You should:',
      options:['Warn the QC informally','Record it and escalate through the reporting line',
               'Ignore it if the goods look acceptable','Ask the factory to redo the paperwork'],
      answer:1, marks:3 },
  ],
};

const PASS_DEFAULT = { pass:60, merit:75, distinction:90, attempts:3, validityMonths:24 };

export default function Training({ db, setDb, saveToDB, currentUser }) {
  const TABS = ['DASHBOARD','COURSE LIBRARY','MY LEARNING','ASSESSMENTS','ENROLMENTS',
                'CERTIFICATIONS','CQB PROGRAMME','ADMIN SETUP'];
  const [tab, setTab] = useState('DASHBOARD');
  const [toast, setToast] = useState(null);
  const notify = (m,t='info') => { setToast({ msg:m, type:t }); setTimeout(()=>setToast(null), 4500); };

  const courses     = db.trainingCourses || [];
  const enrolments  = db.trainingEnrolments || [];
  const attempts    = db.trainingAttempts || [];
  const certs       = db.certifications || [];
  const cqbRules    = db.cqbRules || [];
  const invitations = db.trainingInvitations || [];
  const canAdmin    = isManager(currentUser) || hasRight(currentUser,'ADMIN','edit');

  const myEnrolments = enrolments.filter(e => e.learner === currentUser.name);
  const myCerts      = certs.filter(c => c.learner === currentUser.name);

  const courseOf = code => courses.find(c => c.code === code);
  const learnerPool = useMemo(() => {
    const internal = (db.users||[]).filter(u=>u.status==='Active')
      .map(u=>({ name:u.name, type:'Internal', org:'Toqum', role:u.role, email:u.email }));
    const supplierQAs = [];
    (db.suppliers||[]).forEach(s => (s.qaContacts||[]).forEach(q => {
      if (q.name) supplierQAs.push({ name:q.name, type:'Supplier', org:s.name,
        role:q.designation||'Factory QA', email:q.email });
    }));
    return [...internal, ...supplierQAs];
  }, [db.users, db.suppliers]);

  // ═══════════════════ DASHBOARD ═══════════════════
  const Dashboard = () => {
    const active = enrolments.filter(e=>e.status==='In Progress');
    const done   = enrolments.filter(e=>e.status==='Completed');
    const passRate = attempts.length
      ? Math.round(attempts.filter(a=>a.passed).length/attempts.length*100) : 0;
    const cqbCerts = certs.filter(c=>c.type==='CQB');
    const byCategory = COURSE_CATEGORIES.map(cat => ({
      id:cat, category:cat,
      courses: courses.filter(c=>c.category===cat).length,
      enrolled: enrolments.filter(e=>courseOf(e.courseCode)?.category===cat).length,
      completed: enrolments.filter(e=>e.status==='Completed' && courseOf(e.courseCode)?.category===cat).length,
    })).filter(x=>x.courses);
    return (
      <div>
        <CardRow min={195}>
          <StatCard label="Courses Published" value={courses.filter(c=>c.status!=='Draft').length}
            sub={`${courses.length} total`} color={T.primary} />
          <StatCard label="Active Learners"
            value={new Set(active.map(e=>e.learner)).size} color={T.teal} />
          <StatCard label="In Progress" value={active.length} color={T.warning} />
          <StatCard label="Completed" value={done.length} color={T.success} />
          <StatCard label="Assessment Pass Rate" value={`${passRate}%`}
            sub={`${attempts.length} attempts`} color={T.accent} />
          <StatCard label="Certificates Issued" value={certs.length}
            sub={`${cqbCerts.length} CQB`} color={T.purple} />
          <StatCard label="Pending Invitations"
            value={invitations.filter(i=>i.status==='Invited').length} color={T.orange} />
          <StatCard label="Expiring in 90 days"
            value={certs.filter(c=>c.expiresOn && c.expiresOn <= addDays(today(),90)
              && c.expiresOn >= today()).length} color={T.danger} />
        </CardRow>
        <Split min={430}>
          <Section title="Learning by Category">
            <DataTable columns={['Category','Courses','Enrolments','Completed','Completion']}
              rows={byCategory}
              renderCell={(r,c)=>({ Category:<b>{r.category}</b>, Courses:r.courses,
                Enrolments:r.enrolled, Completed:r.completed,
                Completion:<span style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ width:70, height:6, background:'#f1f5f9', borderRadius:3, display:'inline-block' }}>
                    <span style={{ display:'block', height:6, borderRadius:3, background:T.success,
                      width:`${r.enrolled ? (r.completed/r.enrolled*100) : 0}%` }} /></span>
                  <b>{r.enrolled ? Math.round(r.completed/r.enrolled*100) : 0}%</b></span> }[c])} />
          </Section>
          <Section title="Recent Certifications">
            <DataTable columns={['Issued','Learner','Organisation','Certificate','Grade']}
              rows={certs.slice().reverse().slice(0,12)}
              renderCell={(r,c)=>({ Issued:r.issuedOn, Learner:<b>{r.learner}</b>, Organisation:r.org,
                Certificate: r.type==='CQB'
                  ? <Tag label={`CQB ${r.level} — ${r.brand}`} color={T.purple} />
                  : <Tag label={r.courseCode} color={T.teal} />,
                Grade:<Badge status={r.grade==='Distinction'?'Approved':r.grade==='Merit'?'Passed':'Pending'} /> }[c])}
              emptyMsg="No certificates issued yet." />
          </Section>
        </Split>
      </div>
    );
  };

  // ═══════════════════ COURSE LIBRARY ═══════════════════
  const Library = () => {
    const [cat, setCat] = useState('All');
    const [edit, setEdit] = useState(null);
    const shown = courses.filter(c => cat==='All' || c.category===cat);

    const seedLibrary = async () => {
      if (courses.length && !window.confirm('Add the default course library alongside existing courses?')) return;
      const entries = DEFAULT_COURSES.map((c,i) => ({
        id: uid()+i, ...c, status:'Published',
        content: [
          { type:'video',   title:`${c.title} — recorded session`, url:'', duration:Math.round(c.duration*0.5), required:true },
          { type:'reading', title:`${c.title} — participant handbook`, url:'', duration:Math.round(c.duration*0.3), required:true },
          { type:'podcast', title:`${c.title} — practitioner discussion`, url:'', duration:Math.round(c.duration*0.2), required:false },
        ],
        questions: (SEED_QUESTIONS[c.code]||[]).map((q,qi)=>({ id:`${c.code}-q${qi+1}`, ...q })),
        grading: { ...PASS_DEFAULT },
        audience:['Internal','Supplier'],
        createdBy: currentUser.name, createdOn: today(),
      }));
      setDb(p=>({ ...p, trainingCourses:[...courses, ...entries] }));
      for (const e of entries) await saveToDB('trainingCourse', e);
      notify(`${entries.length} courses added to the library.`, 'success');
    };

    const CourseEditor = ({ course }) => {
      const [f, setF] = useState(course || {
        code:'', title:'', category:COURSE_CATEGORIES[0], level:'Foundation', duration:60,
        description:'', objectives:[], content:[], questions:[], grading:{ ...PASS_DEFAULT },
        audience:['Internal','Supplier'], status:'Draft' });
      const set = (k,v) => setF(p=>({ ...p, [k]:v }));
      const save = async () => {
        if (!f.code || !f.title) return notify('Course code and title are required', 'error');
        const entry = { ...f, id: f.id || uid(), createdBy: f.createdBy || currentUser.name,
          createdOn: f.createdOn || today() };
        setDb(p=>({ ...p, trainingCourses:[...courses.filter(c=>c.id!==entry.id), entry] }));
        await saveToDB('trainingCourse', entry);
        notify('Course saved.', 'success'); setEdit(null);
      };
      const totalMarks = (f.questions||[]).reduce((a,q)=>a+num(q.marks||1),0);
      return (
        <Section title={course ? `Edit — ${course.code}` : 'New Course'}
          actions={<Btn small color="#64748b" label="Close" onClick={()=>setEdit(null)} />}>
          <Grid cols={4}>
            <Field label="Course Code *" value={f.code} onChange={e=>set('code',e.target.value)} placeholder="e.g. RCA-101" />
            <Field label="Title *" value={f.title} onChange={e=>set('title',e.target.value)} />
            <Field label="Category" value={f.category} onChange={e=>set('category',e.target.value)} options={COURSE_CATEGORIES} />
            <Field label="Level" value={f.level} onChange={e=>set('level',e.target.value)} options={LEVELS} />
            <Field label="Duration (minutes)" type="number" value={f.duration} onChange={e=>set('duration',e.target.value)} />
            <Field label="Status" value={f.status} onChange={e=>set('status',e.target.value)} options={['Draft','Published','Retired']} />
            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:11, fontWeight:700, display:'block', marginBottom:4, color:T.textSub }}>Audience</label>
              {['Internal','Supplier'].map(a=>(
                <label key={a} style={{ fontSize:11.5, marginRight:12 }}>
                  <input type="checkbox" checked={(f.audience||[]).includes(a)}
                    onChange={()=>set('audience',(f.audience||[]).includes(a)
                      ? f.audience.filter(x=>x!==a) : [...(f.audience||[]),a])} /> {a}
                </label>
              ))}
            </div>
          </Grid>
          <Field label="Description" type="textarea" value={f.description} onChange={e=>set('description',e.target.value)} />
          <Field label="Learning Objectives (one per line)" type="textarea"
            value={(f.objectives||[]).join('\n')}
            onChange={e=>set('objectives',e.target.value.split('\n').filter(Boolean))} />

          <Collapse title={`Content — ${(f.content||[]).length} items`} defaultOpen>
            <DynamicTable dense
              columns={[
                { label:'Type', key:'type', type:'select', options:CONTENT_TYPES.map(t=>t.key) },
                { label:'Title', key:'title', width:'240px' },
                { label:'URL / File link', key:'url', width:'240px' },
                { label:'Minutes', key:'duration', type:'number', width:'80px' },
                { label:'Required', key:'required', type:'select', options:['Yes','No'] },
              ]}
              data={f.content||[]} setData={v=>set('content',v)}
              onAdd={()=>set('content',[...(f.content||[]),{ type:'video', required:'Yes' }])} />
            <div style={{ fontSize:11.5, color:T.textSub, marginTop:6 }}>
              Paste a YouTube/video/podcast URL above, or upload a PDF/doc directly:
              <label style={{ marginLeft:10, display:'inline-flex', alignItems:'center', gap:6,
                padding:'5px 12px', background:T.primary, color:'white', borderRadius:6,
                fontSize:11, fontWeight:700, cursor:'pointer' }}>
                📎 Upload PDF / File
                <input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                  style={{ display:'none' }}
                  onChange={async e => {
                    const file = e.target.files[0]; if (!file) return;
                    const { uploadFile } = await import('./Shared');
                    const r = await uploadFile(file, { category:'trainingMaterial', docType:'Training' });
                    if (r?.url) {
                      const url = r.url.startsWith('http') ? r.url : 'https://' + r.url.replace(/^\/+/,'');
                      set('content', [...(f.content||[]), {
                        type:'reading', title:file.name.replace(/\.[^.]+$/,''),
                        url, duration:15, required:'Yes'
                      }]);
                    } else {
                      alert('Upload failed — check file size (max 25MB)');
                    }
                    e.target.value = '';
                  }} />
              </label>
            </div>
          </Collapse>

          <Collapse title={`Assessment — ${(f.questions||[]).length} questions, ${totalMarks} marks`} defaultOpen>
            {(f.questions||[]).map((q,i)=>(
              <div key={i} style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:12, marginBottom:10 }}>
                <Grid cols={4}>
                  <Field label={`Question ${i+1}`} value={q.q} style={{ gridColumn:'span 3' }}
                    onChange={e=>set('questions', f.questions.map((x,ix)=>ix===i?{...x,q:e.target.value}:x))} />
                  <Field label="Marks" type="number" value={q.marks||1}
                    onChange={e=>set('questions', f.questions.map((x,ix)=>ix===i?{...x,marks:num(e.target.value)}:x))} />
                </Grid>
                {(q.options||['','','','']).map((opt,oi)=>(
                  <div key={oi} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:5 }}>
                    <input type="radio" name={`ans-${i}`} checked={q.answer===oi}
                      onChange={()=>set('questions', f.questions.map((x,ix)=>ix===i?{...x,answer:oi}:x))} />
                    <input value={opt} placeholder={`Option ${oi+1}${q.answer===oi?' (correct)':''}`}
                      onChange={e=>set('questions', f.questions.map((x,ix)=>ix===i
                        ? { ...x, options:(x.options||['','','','']).map((o,oo)=>oo===oi?e.target.value:o) } : x))}
                      style={{ flex:1, padding:'5px 8px', fontSize:11.5, borderRadius:4,
                        border:`1px solid ${q.answer===oi?T.success:'#cbd5e1'}` }} />
                  </div>
                ))}
                <Btn small outline color={T.danger} label="Remove question"
                  onClick={()=>set('questions', f.questions.filter((_,ix)=>ix!==i))} />
              </div>
            ))}
            <Btn small outline label="+ Add Question"
              onClick={()=>set('questions',[...(f.questions||[]),
                { id:uid(), q:'', options:['','','',''], answer:0, marks:1 }])} />
          </Collapse>

          <Collapse title="Grading Yardstick" defaultOpen>
            <Grid cols={5}>
              <Field label="Pass mark %" type="number" value={f.grading?.pass}
                onChange={e=>set('grading',{...f.grading,pass:num(e.target.value)})} />
              <Field label="Merit at %" type="number" value={f.grading?.merit}
                onChange={e=>set('grading',{...f.grading,merit:num(e.target.value)})} />
              <Field label="Distinction at %" type="number" value={f.grading?.distinction}
                onChange={e=>set('grading',{...f.grading,distinction:num(e.target.value)})} />
              <Field label="Attempts allowed" type="number" value={f.grading?.attempts}
                onChange={e=>set('grading',{...f.grading,attempts:num(e.target.value)})} />
              <Field label="Certificate validity (months)" type="number" value={f.grading?.validityMonths}
                onChange={e=>set('grading',{...f.grading,validityMonths:num(e.target.value)})} />
            </Grid>
          </Collapse>

          <Btn label="Save Course" color={T.success} onClick={save} />
        </Section>
      );
    };

    if (edit !== null) return <CourseEditor course={edit === 'new' ? null : edit} />;

    return (
      <div>
        <Section title={`Course Library (${shown.length})`}
          actions={<span style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            <select value={cat} onChange={e=>setCat(e.target.value)} style={{ fontSize:11, padding:4 }}>
              {['All',...COURSE_CATEGORIES].map(c=><option key={c}>{c}</option>)}
            </select>
            {canAdmin && <>
              <Btn small outline label="Load Default Library" onClick={seedLibrary} />
              <Btn small color={T.success} label="+ New Course" onClick={()=>setEdit('new')} />
            </>}
          </span>}>
          {shown.length===0 && (
            <div style={{ padding:26, textAlign:'center', color:T.textSub, fontSize:12.5 }}>
              No courses yet. {canAdmin && 'Click "Load Default Library" to install 15 ready-made courses covering brand quality systems, root cause analysis, quality tools, project management, conflict management, ethics and feedback — then edit them to match your own material.'}
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(310px,1fr))', gap:14 }}>
            {shown.map(c=>{
              const enrolled = enrolments.filter(e=>e.courseCode===c.code).length;
              const passed = attempts.filter(a=>a.courseCode===c.code && a.passed).length;
              return (
                <div key={c.id} style={{ background:'white', border:`1px solid ${T.border}`,
                  borderTop:`4px solid ${CC[COURSE_CATEGORIES.indexOf(c.category)%CC.length]}`,
                  borderRadius:10, padding:15, boxShadow:T.cardShadow }}>
                  <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                    <div>
                      <div style={{ fontSize:10.5, fontWeight:800, color:T.textSub }}>{c.code}</div>
                      <div style={{ fontWeight:800, fontSize:13.5, margin:'2px 0' }}>{c.title}</div>
                    </div>
                    <Badge status={c.status==='Published'?'Active':'Draft'} />
                  </div>
                  <div style={{ fontSize:11, color:T.textSub, marginBottom:6 }}>
                    {c.category} · {c.level} · {c.duration} min
                  </div>
                  <div style={{ fontSize:11.5, color:T.textMain, marginBottom:8, minHeight:34 }}>
                    {(c.description||'').slice(0,110)}{(c.description||'').length>110?'…':''}
                  </div>
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:8 }}>
                    {CONTENT_TYPES.filter(t=>(c.content||[]).some(x=>x.type===t.key))
                      .map(t=><Tag key={t.key} label={`${t.icon} ${t.label}`} color={t.color} />)}
                    {(c.questions||[]).length>0 && <Tag label={`${c.questions.length} questions`} color={T.textSub} />}
                  </div>
                  <div style={{ fontSize:10.5, color:T.textSub, marginBottom:8 }}>
                    {enrolled} enrolled · {passed} passed · pass mark {c.grading?.pass||60}%
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <Btn small label="Enrol Me" onClick={()=>enrol(currentUser.name,'Internal','Toqum',c.code)} />
                    {canAdmin && <Btn small outline label="Edit" onClick={()=>setEdit(c)} />}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      </div>
    );
  };

  // ── enrolment helper (shared) ──
  const enrol = async (learner, type, org, courseCode, assignedBy) => {
    if (enrolments.some(e=>e.learner===learner && e.courseCode===courseCode && e.status!=='Completed'))
      return notify(`${learner} is already enrolled on ${courseCode}.`, 'warn');
    const c = courseOf(courseCode);
    const entry = { id:uid(), learner, learnerType:type, org, courseCode,
      courseTitle:c?.title||courseCode, assignedBy: assignedBy || currentUser.name,
      enrolledOn: today(), dueDate: addDays(today(), 30), status:'Not Started',
      progress:0, contentDone:[] };
    setDb(p=>({ ...p, trainingEnrolments:[...enrolments, entry] }));
    await saveToDB('trainingEnrolment', entry);
    notify(`${learner} enrolled on ${courseCode}.`, 'success');
  };

  // ═══════════════════ MY LEARNING ═══════════════════
  const MyLearning = () => {
    const [active, setActive] = useState(null);

    const markContent = async (enr, idx) => {
      const doneList = [...(enr.contentDone||[])];
      if (!doneList.includes(idx)) doneList.push(idx);
      const c = courseOf(enr.courseCode);
      const total = (c?.content||[]).length || 1;
      const progress = Math.round(doneList.length/total*100);
      const upd = { ...enr, contentDone:doneList, progress,
        status: progress===100 ? 'Ready for Assessment' : 'In Progress',
        startedOn: enr.startedOn || today() };
      setDb(p=>({ ...p, trainingEnrolments: enrolments.map(x=>x.id===enr.id?upd:x) }));
      await saveToDB('trainingEnrolment', upd);
      setActive(upd);
    };

    if (active) {
      const c = courseOf(active.courseCode);
      if (!c) return <Section title="Course not found"><Btn label="← Back" onClick={()=>setActive(null)} /></Section>;
      const requiredDone = (c.content||[]).every((it,i) =>
        String(it.required)==='No' || (active.contentDone||[]).includes(i));
      return (
        <div>
          <Section title={`${c.code} — ${c.title}`}
            actions={<Btn small color="#64748b" label="← My Learning" onClick={()=>setActive(null)} />}>
            <CardRow min={180} mb={12}>
              <StatCard label="Progress" value={`${active.progress}%`} color={T.primary} />
              <StatCard label="Level" value={c.level} color={T.teal} />
              <StatCard label="Duration" value={`${c.duration} min`} color={T.purple} />
              <StatCard label="Pass Mark" value={`${c.grading?.pass||60}%`} color={T.warning} />
              <StatCard label="Attempts Left"
                value={Math.max(0,(c.grading?.attempts||3) -
                  attempts.filter(a=>a.learner===currentUser.name && a.courseCode===c.code).length)}
                color={T.accent} />
            </CardRow>
            <p style={{ fontSize:12.5 }}>{c.description}</p>
            {(c.objectives||[]).length>0 && <>
              <b style={{ fontSize:12 }}>By the end of this course you will be able to:</b>
              <ul style={{ fontSize:12, marginTop:4 }}>
                {c.objectives.map((o,i)=><li key={i}>{o}</li>)}
              </ul>
            </>}
          </Section>

          <Section title="Course Content">
            {(c.content||[]).map((it,i)=>{
              const t = CONTENT_TYPES.find(x=>x.key===it.type) || CONTENT_TYPES[0];
              const done = (active.contentDone||[]).includes(i);
              return (
                <div key={i} style={{ display:'flex', gap:12, alignItems:'center', padding:'12px 14px',
                  border:`1px solid ${done?T.success:T.border}`, borderRadius:9, marginBottom:9,
                  background: done ? '#00c8750a' : 'white' }}>
                  <span style={{ width:36, height:36, borderRadius:8, background:`${t.color}18`,
                    color:t.color, display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:16, flexShrink:0 }}>{t.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:12.5 }}>{it.title || t.label}</div>
                    <div style={{ fontSize:10.5, color:T.textSub }}>
                      {t.label} · {it.duration||'—'} min · {String(it.required)==='No'?'Optional':'Required'}
                    </div>
                  </div>
                  {it.url
                    ? (() => {
                        // Detect YouTube embed
                        const ytMatch = it.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
                        const spotifyMatch = it.url.includes('spotify.com') || it.url.includes('anchor.fm') || it.url.includes('podcasts.apple.com');

                        // Fix malformed URLs — add https:// if missing
                        const fixUrl = (u) => {
                          if (!u) return u;
                          if (u.startsWith('http://') || u.startsWith('https://')) return u;
                          if (u.startsWith('//')) return 'https:' + u;
                          return 'https://' + u; // handles /toqum.integrashion.com/... style
                        };
                        const safeUrl = fixUrl(it.url);

                        if (ytMatch && (it.type==='video' || !it.type)) {
                          return (
                            <div style={{ marginTop:8, borderRadius:8, overflow:'hidden',
                              maxWidth:560, width:'100%' }}>
                              <div style={{ position:'relative', paddingBottom:'56.25%', height:0 }}>
                                <iframe
                                  style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', borderRadius:8 }}
                                  src={`https://www.youtube.com/embed/${ytMatch[1]}`}
                                  frameBorder="0" allowFullScreen title={it.title} />
                              </div>
                            </div>
                          );
                        }
                        if (spotifyMatch) {
                          return <Btn small outline label="🎧 Open Podcast" onClick={()=>window.open(safeUrl,'_blank')} />;
                        }
                        return <Btn small outline label={`Open ${t.label}`} onClick={()=>window.open(safeUrl,'_blank')} />;
                      })()
                    : <Tag label="no link set" color={T.warning} />}
                  {done ? <Tag label="✓ Completed" color={T.success} />
                        : <Btn small label="Mark Complete" onClick={()=>markContent(active,i)} />}
                </div>
              );
            })}
            {(c.content||[]).length===0 && <span style={{ fontSize:12, color:T.textSub }}>
              No content items added to this course yet.</span>}
          </Section>

          <Section title="Assessment">
            {!requiredDone
              ? <div style={{ fontSize:12.5, color:T.warning }}>
                  Complete all required content items above to unlock the assessment.</div>
              : (c.questions||[]).length===0
                ? <div style={{ fontSize:12.5, color:T.textSub }}>
                    No assessment has been set for this course.</div>
                : <Btn label="Start Assessment" color={T.success}
                    onClick={()=>{ setSitting({ course:c, enrolment:active }); setTab('ASSESSMENTS'); }} />}
          </Section>
        </div>
      );
    }

    return (
      <div>
        <CardRow min={190}>
          <StatCard label="My Courses" value={myEnrolments.length} color={T.primary} />
          <StatCard label="In Progress"
            value={myEnrolments.filter(e=>['In Progress','Not Started'].includes(e.status)).length} color={T.warning} />
          <StatCard label="Ready for Assessment"
            value={myEnrolments.filter(e=>e.status==='Ready for Assessment').length} color={T.teal} />
          <StatCard label="Completed"
            value={myEnrolments.filter(e=>e.status==='Completed').length} color={T.success} />
          <StatCard label="My Certificates" value={myCerts.length} color={T.purple} />
        </CardRow>
        <Section title="My Learning">
          <DataTable columns={['Course','Title','Category','Level','Assigned','Due','Progress','Status']}
            rows={myEnrolments}
            renderCell={(r,c)=>{
              const co = courseOf(r.courseCode);
              return ({ Course:<b>{r.courseCode}</b>, Title:r.courseTitle,
                Category:co?.category||'—', Level:co?.level||'—',
                Assigned:r.assignedBy, Due:<span style={{ color: r.status!=='Completed'
                  && r.dueDate<today() ? T.danger : 'inherit', fontWeight:600 }}>{r.dueDate}</span>,
                Progress:<span style={{ display:'flex', alignItems:'center', gap:7 }}>
                  <span style={{ width:60, height:6, background:'#f1f5f9', borderRadius:3, display:'inline-block' }}>
                    <span style={{ display:'block', height:6, width:`${r.progress}%`, borderRadius:3,
                      background: r.progress===100?T.success:T.primary }} /></span>{r.progress}%</span>,
                Status:<Badge status={r.status==='Completed'?'Completed':r.status==='Ready for Assessment'?'Pending':'In Progress'} /> })[c];
            }}
            actions={r => <Btn small label="Open" onClick={()=>setActive(r)} />}
            emptyMsg="You have no course enrolments. Browse the Course Library and enrol." />
        </Section>
        {myCerts.length>0 && (
          <Section title="My Certificates">
            <DataTable columns={['Issued','Certificate','Grade','Score','Valid Until']}
              rows={myCerts}
              renderCell={(r,c)=>({ Issued:r.issuedOn,
                Certificate: r.type==='CQB'
                  ? <b>CQB {r.level} — {r.brand}</b> : <b>{r.courseCode} — {r.courseTitle}</b>,
                Grade:<Badge status={r.grade==='Distinction'?'Approved':'Passed'} />,
                Score:`${r.score}%`,
                'Valid Until':<span style={{ color: r.expiresOn && r.expiresOn<today() ? T.danger : 'inherit' }}>
                  {r.expiresOn||'—'}</span> }[c])}
              actions={r => <Btn small outline label="🖨 Print" onClick={()=>printCert(r)} />} />
          </Section>
        )}
      </div>
    );
  };

  // ═══════════════════ ASSESSMENT SITTING ═══════════════════
  const [sitting, setSitting] = useState(null);

  const Assessments = () => {
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);

    if (!sitting) {
      const mine = attempts.filter(a=>a.learner===currentUser.name);
      return (
        <div>
          <Section title="Assessments Available">
            <DataTable columns={['Course','Title','Questions','Pass Mark','Attempts Used','Status']}
              rows={myEnrolments.filter(e=>e.status==='Ready for Assessment'||e.status==='Completed')}
              renderCell={(r,c)=>{
                const co = courseOf(r.courseCode);
                const used = attempts.filter(a=>a.learner===currentUser.name && a.courseCode===r.courseCode).length;
                return ({ Course:<b>{r.courseCode}</b>, Title:r.courseTitle,
                  Questions:(co?.questions||[]).length, 'Pass Mark':`${co?.grading?.pass||60}%`,
                  'Attempts Used':`${used}/${co?.grading?.attempts||3}`,
                  Status:<Badge status={r.status==='Completed'?'Completed':'Pending'} /> })[c];
              }}
              actions={r => {
                const co = courseOf(r.courseCode);
                const used = attempts.filter(a=>a.learner===currentUser.name && a.courseCode===r.courseCode).length;
                const left = (co?.grading?.attempts||3) - used;
                return left>0 && (co?.questions||[]).length>0
                  ? <Btn small label={`Sit Assessment (${left} left)`}
                      onClick={()=>{ setSitting({ course:co, enrolment:r }); setAnswers({}); setResult(null); }} />
                  : <Tag label={left<=0?'No attempts left':'No questions set'} color={T.textSub} />;
              }}
              emptyMsg="Complete a course's content to unlock its assessment." />
          </Section>
          <Section title="My Attempt History">
            <DataTable columns={['Date','Course','Score','Marks','Grade','Result']} rows={mine.slice().reverse()}
              renderCell={(r,c)=>({ Date:r.date, Course:<b>{r.courseCode}</b>, Score:`${r.score}%`,
                Marks:`${r.earned}/${r.total}`, Grade:r.grade||'—',
                Result:<Badge status={r.passed?'Passed':'Failed'} /> }[c])}
              emptyMsg="No attempts yet." />
          </Section>
        </div>
      );
    }

    const c = sitting.course;
    const qs = c.questions || [];
    const totalMarks = qs.reduce((a,q)=>a+num(q.marks||1),0);

    const submit = async () => {
      const unanswered = qs.filter((q,i)=>answers[i]===undefined).length;
      if (unanswered && !window.confirm(`${unanswered} question(s) unanswered. Submit anyway?`)) return;
      let earned = 0;
      const detail = qs.map((q,i)=>{
        const correct = answers[i] === q.answer;
        if (correct) earned += num(q.marks||1);
        return { q:q.q, chosen:answers[i], correct:q.answer, right:correct, marks:num(q.marks||1) };
      });
      const score = totalMarks ? Math.round(earned/totalMarks*100) : 0;
      const g = c.grading || PASS_DEFAULT;
      const passed = score >= (g.pass||60);
      const grade = score >= (g.distinction||90) ? 'Distinction'
                  : score >= (g.merit||75) ? 'Merit' : passed ? 'Pass' : 'Fail';
      const att = { id:uid(), learner:currentUser.name,
        learnerType: sitting.enrolment.learnerType || 'Internal',
        org: sitting.enrolment.org || 'Toqum',
        courseCode:c.code, courseTitle:c.title, date:today(),
        earned, total:totalMarks, score, grade, passed, detail };
      setDb(p=>({ ...p, trainingAttempts:[...attempts, att] }));
      await saveToDB('trainingAttempt', att);

      if (passed) {
        const upd = { ...sitting.enrolment, status:'Completed', progress:100,
          completedOn:today(), score, grade };
        setDb(p=>({ ...p, trainingEnrolments: enrolments.map(x=>x.id===upd.id?upd:x) }));
        await saveToDB('trainingEnrolment', upd);
        const cert = { id:uid(), type:'COURSE', learner:currentUser.name,
          learnerType: att.learnerType, org: att.org,
          courseCode:c.code, courseTitle:c.title, score, grade,
          issuedOn:today(),
          expiresOn: g.validityMonths ? addDays(today(), g.validityMonths*30) : null,
          certificateNo:`TQ-${c.code}-${String(uid()).slice(-6)}`, issuedBy:'Toqum Quality Academy' };
        setDb(p=>({ ...p, certifications:[...certs, cert] }));
        await saveToDB('certification', cert);
      }
      setResult({ score, grade, passed, earned, totalMarks, detail });
    };

    if (result) {
      return (
        <Section title={`Result — ${c.code}`}
          actions={<Btn small color="#64748b" label="← Back" onClick={()=>{ setSitting(null); setResult(null); }} />}>
          <div style={{ background: result.passed?'#00c87512':'#e2445c12',
            border:`2px solid ${result.passed?T.success:T.danger}`, borderRadius:12,
            padding:'22px 26px', textAlign:'center', marginBottom:16 }}>
            <div style={{ fontSize:38, fontWeight:900, color: result.passed?T.success:T.danger }}>
              {result.score}%</div>
            <div style={{ fontSize:15, fontWeight:800, marginTop:4 }}>
              {result.grade} — {result.passed ? 'PASSED' : 'NOT PASSED'}</div>
            <div style={{ fontSize:12, color:T.textSub, marginTop:4 }}>
              {result.earned} of {result.totalMarks} marks · pass mark {c.grading?.pass||60}%</div>
            {result.passed && <div style={{ fontSize:12, color:T.success, fontWeight:700, marginTop:8 }}>
              Certificate issued — see My Learning</div>}
          </div>
          <b style={{ fontSize:12 }}>Answer Review</b>
          {result.detail.map((d,i)=>(
            <div key={i} style={{ padding:'9px 12px', border:`1px solid ${d.right?T.success:T.danger}`,
              borderRadius:8, marginTop:8, background: d.right?'#00c8750a':'#e2445c0a' }}>
              <div style={{ fontSize:12, fontWeight:700 }}>{i+1}. {d.q}</div>
              <div style={{ fontSize:11.5, color:T.textSub, marginTop:3 }}>
                Your answer: {d.chosen !== undefined ? qs[i].options[d.chosen] : '— not answered —'}
                {!d.right && <> · Correct: <b style={{ color:T.success }}>{qs[i].options[d.correct]}</b></>}
              </div>
            </div>
          ))}
        </Section>
      );
    }

    return (
      <Section title={`Assessment — ${c.code} · ${c.title}`}
        actions={<Btn small color="#64748b" label="Abandon" onClick={()=>setSitting(null)} />}>
        <div style={{ fontSize:12, color:T.textSub, background:'#f8fafc', padding:'10px 14px',
          borderRadius:8, marginBottom:14 }}>
          {qs.length} questions · {totalMarks} marks · pass mark {c.grading?.pass||60}% ·
          merit {c.grading?.merit||75}% · distinction {c.grading?.distinction||90}%
        </div>
        {qs.map((q,i)=>(
          <div key={i} style={{ border:`1px solid ${T.border}`, borderRadius:9, padding:14, marginBottom:11 }}>
            <div style={{ fontWeight:700, fontSize:12.5, marginBottom:8 }}>
              {i+1}. {q.q} <span style={{ color:T.textSub, fontWeight:500 }}>({q.marks||1} mark{(q.marks||1)>1?'s':''})</span>
            </div>
            {(q.options||[]).map((o,oi)=>(
              <label key={oi} style={{ display:'flex', gap:9, alignItems:'center', padding:'6px 9px',
                borderRadius:6, cursor:'pointer', fontSize:12,
                background: answers[i]===oi ? '#0073ea12' : 'transparent',
                border:`1px solid ${answers[i]===oi ? T.primary : 'transparent'}`, marginBottom:3 }}>
                <input type="radio" name={`q${i}`} checked={answers[i]===oi}
                  onChange={()=>setAnswers(p=>({ ...p, [i]:oi }))} />
                {o}
              </label>
            ))}
          </div>
        ))}
        <Btn label="Submit Assessment" color={T.success} onClick={submit} />
      </Section>
    );
  };

  // ═══════════════════ ENROLMENTS & INVITATIONS ═══════════════════
  const Enrolments = () => {
    const [f, setF] = useState({ learner:'', courseCode:'', dueDate: addDays(today(),30) });
    const [invite, setInvite] = useState({ name:'', email:'', org:'', role:'Factory QA',
      brand:'', programme:'CQB' });

    const assign = async () => {
      if (!canAdmin) return notify('Manager rights required to assign training.', 'error');
      if (!f.learner || !f.courseCode) return notify('Select a learner and a course', 'error');
      const l = learnerPool.find(x=>x.name===f.learner);
      await enrol(f.learner, l?.type||'Internal', l?.org||'Toqum', f.courseCode, currentUser.name);
      setF({ learner:'', courseCode:'', dueDate: addDays(today(),30) });
    };

    const sendInvite = async () => {
      if (!invite.name || !invite.email) return notify('Name and email are required', 'error');
      const entry = { id:uid(), ...invite, invitedBy:currentUser.name, invitedOn:today(),
        status:'Invited', history:[{ date:today(), event:'Invitation sent', by:currentUser.name }] };
      setDb(p=>({ ...p, trainingInvitations:[...invitations, entry] }));
      await saveToDB('trainingInvitation', entry);
      try {
        await apiPost('send_mail.php', { to:[invite.email], cc:[],
          subject:`Invitation — Toqum Quality Academy (${invite.programme})`,
          body:`Dear ${invite.name},\n\nYou have been invited to join the Toqum Quality Academy `
             + `${invite.programme === 'CQB' ? 'Certified QA of Brand (CQB) programme'
                                             : 'training programme'}`
             + `${invite.brand ? ` for ${invite.brand}` : ''}.\n\n`
             + `Organisation : ${invite.org}\nRole         : ${invite.role}\n\n`
             + `Your account will be created shortly and you will receive login details separately.\n\n`
             + `Regards,\n${currentUser.name}\nQuality Assurance`,
          refType:'training', refId:entry.id, sentBy:currentUser.name });
        notify('Invitation sent.', 'success');
      } catch { notify('Invitation recorded — mail server unreachable.', 'warn'); }
      setInvite({ name:'', email:'', org:'', role:'Factory QA', brand:'', programme:'CQB' });
    };

    const setInviteStatus = async (inv, status) => {
      const upd = { ...inv, status,
        history:[...(inv.history||[]), { date:today(), event:`Status → ${status}`, by:currentUser.name }] };
      setDb(p=>({ ...p, trainingInvitations: invitations.map(x=>x.id===inv.id?upd:x) }));
      await saveToDB('trainingInvitation', upd);
    };

    return (
      <div>
        <Section title="Assign a Course">
          <Grid cols={4}>
            <Field label="Learner" value={f.learner} onChange={e=>setF({...f,learner:e.target.value})}
              options={learnerPool.map(l=>l.name)} />
            <Field label="Course" value={f.courseCode} onChange={e=>setF({...f,courseCode:e.target.value})}
              options={courses.filter(c=>c.status==='Published').map(c=>`${c.code}`)} />
            <Field label="Due Date" type="date" value={f.dueDate} onChange={e=>setF({...f,dueDate:e.target.value})} />
            <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}>
              <Btn label="Assign" onClick={assign} disabled={!canAdmin} />
            </div>
          </Grid>
          {f.learner && (
            <div style={{ fontSize:11.5, color:T.textSub }}>
              {(() => { const l = learnerPool.find(x=>x.name===f.learner);
                return l ? `${l.type} · ${l.org} · ${l.role}` : ''; })()}
            </div>
          )}
        </Section>

        <Section title="Invite an External Candidate (supplier QA)">
          <Grid cols={6}>
            <Field label="Name" value={invite.name} onChange={e=>setInvite({...invite,name:e.target.value})} />
            <Field label="Email" value={invite.email} onChange={e=>setInvite({...invite,email:e.target.value})} />
            <Field label="Organisation" value={invite.org} onChange={e=>setInvite({...invite,org:e.target.value})}
              options={(db.suppliers||[]).map(s=>s.name)} />
            <Field label="Role" value={invite.role} onChange={e=>setInvite({...invite,role:e.target.value})}
              options={['Factory QA','QA Manager','Line QC','Final Auditor','Production Manager']} />
            <Field label="Brand (for CQB)" value={invite.brand} onChange={e=>setInvite({...invite,brand:e.target.value})}
              options={(db.brands||[]).map(b=>b.name||b)} />
            <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}>
              <Btn label="Send Invitation" color={T.purple} onClick={sendInvite} />
            </div>
          </Grid>
        </Section>

        <Section title="Invitation Register">
          <DataTable columns={['Invited','Candidate','Organisation','Role','Brand','Programme','Status']}
            rows={invitations.slice().reverse()}
            renderCell={(r,c)=>({ Invited:r.invitedOn, Candidate:<b>{r.name}</b>, Organisation:r.org,
              Role:r.role, Brand:r.brand||'—', Programme:<Tag label={r.programme} color={T.purple} />,
              Status:<Badge status={r.status==='Enrolled'?'Active':r.status==='Declined'?'Rejected':'Pending'} /> }[c])}
            actions={r => r.status==='Invited' && <span style={{ display:'flex', gap:5 }}>
              <Btn small color={T.success} label="Accepted" onClick={()=>setInviteStatus(r,'Enrolled')} />
              <Btn small outline color={T.danger} label="Declined" onClick={()=>setInviteStatus(r,'Declined')} />
            </span>}
            emptyMsg="No invitations sent yet." />
        </Section>

        <Section title="All Enrolments">
          <DataTable columns={['Enrolled','Learner','Type','Organisation','Course','Assigned By','Due','Progress','Status']}
            rows={enrolments.slice().reverse()}
            renderCell={(r,c)=>({ Enrolled:r.enrolledOn, Learner:<b>{r.learner}</b>,
              Type:<Tag label={r.learnerType} color={r.learnerType==='Supplier'?T.purple:T.teal} />,
              Organisation:r.org, Course:r.courseCode, 'Assigned By':r.assignedBy,
              Due:<span style={{ color: r.status!=='Completed' && r.dueDate<today() ? T.danger:'inherit' }}>{r.dueDate}</span>,
              Progress:`${r.progress}%`,
              Status:<Badge status={r.status==='Completed'?'Completed':'In Progress'} /> }[c])}
            emptyMsg="No enrolments yet." />
        </Section>
      </div>
    );
  };

  // ═══════════════════ CERTIFICATES ═══════════════════
  const printCert = (cert) => printHTML(`Certificate — ${cert.learner}`, `
    <div style="border:8px double #14181f;padding:38px;text-align:center;font-family:Georgia,serif">
      <div style="font-size:11px;letter-spacing:5px;color:#666">TOQUM QUALITY ACADEMY</div>
      <h1 style="font-size:26px;margin:14px 0 4px;letter-spacing:1px">
        ${cert.type==='CQB' ? 'CERTIFIED QA OF BRAND' : 'CERTIFICATE OF COMPLETION'}</h1>
      ${cert.type==='CQB' ? `<div style="font-size:15px;color:#0073ea;font-weight:bold">
        ${cert.brand} · ${cert.level} Level</div>` : ''}
      <div style="font-size:11px;color:#666;margin:18px 0 6px">This is to certify that</div>
      <div style="font-size:24px;font-weight:bold;margin:6px 0">${cert.learner}</div>
      <div style="font-size:12px;color:#444">${cert.org||''}</div>
      <div style="font-size:11px;color:#666;margin:18px 0 6px">
        has successfully completed${cert.type==='CQB' ? ' the requirements of' : ''}</div>
      <div style="font-size:16px;font-weight:bold;margin:6px 0">
        ${cert.type==='CQB' ? `the Certified QA of Brand programme for ${cert.brand}`
                            : `${cert.courseCode} — ${cert.courseTitle}`}</div>
      <div style="font-size:13px;margin:14px 0">
        Grade: <b>${cert.grade}</b>${cert.score?` · Score: <b>${cert.score}%</b>`:''}</div>
      ${cert.courses ? `<div style="font-size:10px;color:#555;margin:10px 0">
        Component courses: ${cert.courses.join(' · ')}</div>` : ''}
      <table style="width:100%;margin-top:34px;font-size:10px;color:#555;border:none">
        <tr>
          <td style="border:none;text-align:left">Certificate No: <b>${cert.certificateNo}</b><br>
              Issued: ${cert.issuedOn}${cert.expiresOn?`<br>Valid until: ${cert.expiresOn}`:''}</td>
          <td style="border:none;text-align:right">_______________________<br>
              Quality Assurance Director<br>${cert.issuedBy||'Toqum Quality Academy'}</td>
        </tr>
      </table>
    </div>`);

  const Certificates = () => {
    const [filter, setFilter] = useState('All');
    const shown = certs.filter(c => filter==='All' ||
      (filter==='CQB' ? c.type==='CQB' : filter==='Course' ? c.type!=='CQB' :
       filter==='Expiring' ? c.expiresOn && c.expiresOn<=addDays(today(),90) && c.expiresOn>=today() :
       filter==='Expired' ? c.expiresOn && c.expiresOn<today() : true));
    return (
      <Section title={`Certificates Issued (${shown.length})`}
        actions={<select value={filter} onChange={e=>setFilter(e.target.value)} style={{ fontSize:11, padding:4 }}>
          {['All','CQB','Course','Expiring','Expired'].map(o=><option key={o}>{o}</option>)}
        </select>}>
        <DataTable columns={['Certificate No','Issued','Learner','Type','Organisation','Certificate','Grade','Score','Valid Until']}
          rows={shown.slice().reverse()}
          renderCell={(r,c)=>({
            'Certificate No':<code style={{ fontSize:11 }}>{r.certificateNo}</code>,
            Issued:r.issuedOn, Learner:<b>{r.learner}</b>,
            Type:<Tag label={r.learnerType||'Internal'} color={r.learnerType==='Supplier'?T.purple:T.teal} />,
            Organisation:r.org,
            Certificate: r.type==='CQB'
              ? <b style={{ color:T.purple }}>CQB {r.level} — {r.brand}</b>
              : `${r.courseCode} — ${r.courseTitle}`,
            Grade:<Badge status={r.grade==='Distinction'?'Approved':'Passed'} />,
            Score: r.score ? `${r.score}%` : '—',
            'Valid Until': r.expiresOn
              ? <span style={{ color: r.expiresOn<today() ? T.danger
                  : r.expiresOn<=addDays(today(),90) ? T.warning : 'inherit', fontWeight:600 }}>
                  {r.expiresOn}</span> : '—',
          }[c])}
          actions={r => <Btn small outline label="🖨 Print" onClick={()=>printCert(r)} />}
          emptyMsg="No certificates issued yet." />
      </Section>
    );
  };

  // ═══════════════════ CQB PROGRAMME ═══════════════════
  const CQB = () => {
    const [rule, setRule] = useState({ brand:'', level:'Foundation', requiredCourses:[],
      minAverage:70, minExperienceMonths:6, requiresAudit:'No', validityMonths:24 });

    const saveRule = async () => {
      if (!canAdmin) return notify('Manager rights required.', 'error');
      if (!rule.brand || !rule.requiredCourses.length)
        return notify('Choose a brand and at least one required course', 'error');
      const entry = { id:uid(), ...rule, createdBy:currentUser.name, createdOn:today() };
      setDb(p=>({ ...p, cqbRules:[...cqbRules.filter(r =>
        !(r.brand===rule.brand && r.level===rule.level)), entry] }));
      await saveToDB('cqbRule', entry);
      notify(`CQB rule saved for ${rule.brand} — ${rule.level}.`, 'success');
      setRule({ brand:'', level:'Foundation', requiredCourses:[], minAverage:70,
        minExperienceMonths:6, requiresAudit:'No', validityMonths:24 });
    };

    // Who qualifies right now?
    const candidates = useMemo(() => {
      const supplierLearners = [...new Set(enrolments
        .filter(e=>e.learnerType==='Supplier').map(e=>e.learner))];
      return supplierLearners.map(name => {
        const done = enrolments.filter(e=>e.learner===name && e.status==='Completed');
        const scores = done.map(e=>num(e.score)).filter(Boolean);
        const avg = scores.length ? Math.round(scores.reduce((a,b)=>a+b,0)/scores.length) : 0;
        const org = enrolments.find(e=>e.learner===name)?.org || '';
        const held = certs.filter(c=>c.type==='CQB' && c.learner===name);
        const eligible = cqbRules.filter(r =>
          r.requiredCourses.every(rc => done.some(d=>d.courseCode===rc)) && avg >= num(r.minAverage));
        return { id:name, name, org, completed:done.length, avg,
          courses:done.map(d=>d.courseCode), eligible, held };
      });
    }, [enrolments, certs, cqbRules]);

    const award = async (cand, r) => {
      if (!canAdmin) return notify('Manager rights required to award CQB.', 'error');
      const cert = { id:uid(), type:'CQB', learner:cand.name, learnerType:'Supplier', org:cand.org,
        brand:r.brand, level:r.level, score:cand.avg, grade: cand.avg>=90?'Distinction':cand.avg>=75?'Merit':'Pass',
        courses:cand.courses, issuedOn:today(),
        expiresOn: addDays(today(), (r.validityMonths||24)*30),
        certificateNo:`CQB-${r.brand.slice(0,3).toUpperCase()}-${String(uid()).slice(-6)}`,
        issuedBy:'Toqum Quality Academy', awardedBy:currentUser.name };
      setDb(p=>({ ...p, certifications:[...certs, cert] }));
      await saveToDB('certification', cert);
      notify(`${cand.name} certified as CQB ${r.level} for ${r.brand}.`, 'success');
    };

    return (
      <div>
        <div style={{ background:'#a25ddc12', border:`1px solid ${T.purple}40`, borderRadius:10,
          padding:'14px 18px', marginBottom:16, fontSize:12.5 }}>
          <b style={{ color:T.purple, fontSize:13 }}>Certified QA of Brand (CQB)</b><br/>
          A dedicated credential for supplier QAs working on a specific brand's orders. You define
          which courses a candidate must complete and at what average, per brand and per level.
          Candidates who meet the bar appear below for award.
        </div>

        <CardRow min={200}>
          <StatCard label="CQB Rules Defined" value={cqbRules.length} color={T.purple} />
          <StatCard label="Supplier Learners"
            value={new Set(enrolments.filter(e=>e.learnerType==='Supplier').map(e=>e.learner)).size} color={T.teal} />
          <StatCard label="Eligible Now"
            value={candidates.filter(c=>c.eligible.length && !c.held.length).length} color={T.warning} />
          <StatCard label="CQB Certified" value={certs.filter(c=>c.type==='CQB').length} color={T.success} />
        </CardRow>

        {canAdmin && (
          <Section title="Define a CQB Rule — which courses entitle a supplier QA to certification, and at what level">
            <Grid cols={5}>
              <Field label="Brand" value={rule.brand} onChange={e=>setRule({...rule,brand:e.target.value})}
                options={(db.brands||[]).map(b=>b.name||b)} />
              <Field label="CQB Level" value={rule.level} onChange={e=>setRule({...rule,level:e.target.value})}
                options={LEVELS} />
              <Field label="Minimum Average Score %" type="number" value={rule.minAverage}
                onChange={e=>setRule({...rule,minAverage:e.target.value})} />
              <Field label="Minimum Experience (months)" type="number" value={rule.minExperienceMonths}
                onChange={e=>setRule({...rule,minExperienceMonths:e.target.value})} />
              <Field label="Certificate Validity (months)" type="number" value={rule.validityMonths}
                onChange={e=>setRule({...rule,validityMonths:e.target.value})} />
            </Grid>
            <b style={{ fontSize:11.5 }}>Required Courses</b>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',
              gap:6, margin:'6px 0 12px' }}>
              {courses.filter(c=>c.status==='Published').map(c=>(
                <label key={c.code} style={{ fontSize:11.5, display:'flex', gap:7, alignItems:'center',
                  padding:'6px 9px', borderRadius:6, cursor:'pointer',
                  border:`1.5px solid ${rule.requiredCourses.includes(c.code)?T.purple:T.border}`,
                  background: rule.requiredCourses.includes(c.code)?'#a25ddc10':'white' }}>
                  <input type="checkbox" checked={rule.requiredCourses.includes(c.code)}
                    onChange={()=>setRule(p=>({ ...p, requiredCourses: p.requiredCourses.includes(c.code)
                      ? p.requiredCourses.filter(x=>x!==c.code) : [...p.requiredCourses, c.code] }))} />
                  <span><b>{c.code}</b> — {c.title.slice(0,34)}{c.title.length>34?'…':''}</span>
                </label>
              ))}
            </div>
            <Btn label="Save CQB Rule" color={T.purple} onClick={saveRule} />
          </Section>
        )}

        <Section title="CQB Rules">
          <DataTable columns={['Brand','Level','Required Courses','Min Average','Min Experience','Validity']}
            rows={cqbRules}
            renderCell={(r,c)=>({ Brand:<b>{r.brand}</b>, Level:<Tag label={r.level} color={T.purple} />,
              'Required Courses':<span>{r.requiredCourses.map(rc=><Tag key={rc} label={rc} color={T.teal} />)}</span>,
              'Min Average':`${r.minAverage}%`, 'Min Experience':`${r.minExperienceMonths} months`,
              Validity:`${r.validityMonths} months` }[c])}
            actions={r => canAdmin && <Btn small color={T.danger} label="✕"
              onClick={()=>setDb(p=>({ ...p, cqbRules: cqbRules.filter(x=>x.id!==r.id) }))} />}
            emptyMsg="No CQB rules defined yet. Define one above to open the pathway." />
        </Section>

        <Section title="Candidate Progress & Eligibility">
          <DataTable columns={['Candidate','Organisation','Courses Completed','Average','Eligible For','Currently Held']}
            rows={candidates}
            renderCell={(r,c)=>({
              Candidate:<b>{r.name}</b>, Organisation:r.org, 'Courses Completed':r.completed,
              Average:<b style={{ color: r.avg>=75?T.success:r.avg>=60?T.warning:T.danger }}>{r.avg}%</b>,
              'Eligible For': r.eligible.length
                ? r.eligible.map(e=><Tag key={e.id} label={`${e.brand} ${e.level}`} color={T.success} />)
                : <span style={{ fontSize:11, color:T.textSub }}>not yet</span>,
              'Currently Held': r.held.length
                ? r.held.map(h=><Tag key={h.id} label={`${h.brand} ${h.level}`} color={T.purple} />)
                : '—',
            }[c])}
            actions={r => r.eligible.filter(e=>!r.held.some(h=>h.brand===e.brand && h.level===e.level))
              .map(e => <Btn key={e.id} small color={T.purple} label={`Award ${e.level}`}
                onClick={()=>award(r,e)} />)}
            emptyMsg="No supplier QAs enrolled yet. Invite candidates from the Enrolments tab." />
        </Section>
      </div>
    );
  };

  // ═══════════════════ ADMIN SETUP ═══════════════════
  const AdminSetup = () => {
    const [defaults, setDefaults] = useState(db.trainingDefaults || PASS_DEFAULT);
    const save = async () => {
      setDb(p=>({ ...p, trainingDefaults: defaults }));
      await saveToDB('trainingDefault', { id:'default', ...defaults });
      notify('Default grading yardstick saved.', 'success');
    };
    const attemptStats = COURSE_CATEGORIES.map(cat => {
      const codes = courses.filter(c=>c.category===cat).map(c=>c.code);
      const at = attempts.filter(a=>codes.includes(a.courseCode));
      return { id:cat, category:cat, attempts:at.length,
        passed:at.filter(a=>a.passed).length,
        avg: at.length ? Math.round(at.reduce((a,b)=>a+num(b.score),0)/at.length) : 0 };
    }).filter(x=>x.attempts);
    return (
      <div>
        {!canAdmin && <div style={{ fontSize:12.5, color:T.warning, marginBottom:12 }}>
          View only — manager rights are required to change these settings.</div>}
        <Section title="Default Grading Yardstick — applied to new courses">
          <Grid cols={5}>
            <Field label="Pass mark %" type="number" value={defaults.pass}
              onChange={e=>setDefaults({...defaults,pass:num(e.target.value)})} disabled={!canAdmin} />
            <Field label="Merit at %" type="number" value={defaults.merit}
              onChange={e=>setDefaults({...defaults,merit:num(e.target.value)})} disabled={!canAdmin} />
            <Field label="Distinction at %" type="number" value={defaults.distinction}
              onChange={e=>setDefaults({...defaults,distinction:num(e.target.value)})} disabled={!canAdmin} />
            <Field label="Attempts allowed" type="number" value={defaults.attempts}
              onChange={e=>setDefaults({...defaults,attempts:num(e.target.value)})} disabled={!canAdmin} />
            <Field label="Certificate validity (months)" type="number" value={defaults.validityMonths}
              onChange={e=>setDefaults({...defaults,validityMonths:num(e.target.value)})} disabled={!canAdmin} />
          </Grid>
          <Btn label="Save Defaults" color={T.success} onClick={save} disabled={!canAdmin} />
          <div style={{ fontSize:11.5, color:T.textSub, marginTop:8 }}>
            Individual courses can override these in the course editor. Changing the defaults does not
            alter courses that already exist.
          </div>
        </Section>
        <Section title="Assessment Performance by Category">
          <DataTable columns={['Category','Attempts','Passed','Pass Rate','Average Score']}
            rows={attemptStats}
            renderCell={(r,c)=>({ Category:<b>{r.category}</b>, Attempts:r.attempts, Passed:r.passed,
              'Pass Rate':<b style={{ color: r.attempts && r.passed/r.attempts>=0.7?T.success:T.warning }}>
                {r.attempts?Math.round(r.passed/r.attempts*100):0}%</b>,
              'Average Score':`${r.avg}%` }[c])}
            emptyMsg="No assessment attempts recorded yet." />
        </Section>
      </div>
    );
  };

  return (
    <Page title="TRAINING & CERTIFICATION"
      subtitle="Course library · assessments · certificates · Certified QA of Brand pathway">
      <TabBar tabs={TABS} active={tab} onChange={t=>{ setTab(t); if (t!=='ASSESSMENTS') setSitting(null); }} />
      {tab==='DASHBOARD'      && <Dashboard />}
      {tab==='COURSE LIBRARY' && <Library />}
      {tab==='MY LEARNING'    && <MyLearning />}
      {tab==='ASSESSMENTS'    && <Assessments />}
      {tab==='ENROLMENTS'     && <Enrolments />}
      {tab==='CERTIFICATIONS' && <Certificates />}
      {tab==='CQB PROGRAMME'  && <CQB />}
      {tab==='ADMIN SETUP'    && <AdminSetup />}
      <Toast msg={toast?.msg} type={toast?.type} onClose={()=>setToast(null)} />
    </Page>
  );
}
